import { useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { selectedCompanyIdProfileAtom } from '../../../state/UIStateJ';
import { metricsFundDataByFundIdAtomJ } from '../../../../../services/state/CompanyMetricsStateJ';
import { IFundData } from '../../../../../data-models/scenario.data-model';
import { RoundParticipationType } from './RoundParticipationToggle';

export function useRoundParticipation() {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const fundDataByFundId = useAtomValue(metricsFundDataByFundIdAtomJ(companyId));
  const allFunds = Array.from(fundDataByFundId.values());

  const getNoneParticipationValues = useCallback(() => {
    return allFunds.map((fund) => {
      return {
        fundId: fund.fundId,
        investedAmount: 0,
      };
    });
  }, [allFunds]);

  const isNoneParticipation = useCallback(
    (fundData: Partial<IFundData>[]) => {
      return (
        fundData.length == fundDataByFundId.size &&
        fundData.every((formDataFund) => {
          const fund = fundDataByFundId.get(formDataFund?.fundId ?? -1);
          if (!fund || typeof formDataFund.investedAmount !== 'number') {
            return false;
          }
          return formDataFund.investedAmount === 0;
        })
      );
    },
    [fundDataByFundId]
  );

  const getProRataValues = useCallback(
    (roundSize: number): IFundData[] => {
      return allFunds.map((fund) => ({
        fundId: fund.fundId,
        investedAmount: (roundSize * (fund.ownerShipPercentage ?? 0)) / 100,
      }));
    },
    [allFunds]
  );

  const isProRata = useCallback(
    (roundSize: number, fundData: Partial<IFundData>[]) => {
      return (
        fundData.length == fundDataByFundId.size &&
        fundData.every((formDataFund) => {
          const fund = fundDataByFundId.get(formDataFund?.fundId ?? -1);
          if (!fund || typeof formDataFund.investedAmount !== 'number') {
            return false;
          }
          return (
            Math.round(formDataFund.investedAmount * 100) ===
            Math.round((fund?.ownerShipPercentage ?? 0) * roundSize)
          );
        })
      );
    },
    [fundDataByFundId]
  );

  const getInitialParticipation = useCallback(
    (roundSize: number, fundData: Partial<IFundData>[]): RoundParticipationType => {
      // `[]` and `[{}]` are both "nothing chosen yet" — lodash merge in
      // getInitialScenarioValues can turn the former into the latter.
      if (!fundData.some((fund) => fund?.fundId != null)) {
        return RoundParticipationType.none;
      }
      if (isProRata(roundSize, fundData)) {
        return RoundParticipationType.proRata;
      } else if (isNoneParticipation(fundData)) {
        return RoundParticipationType.none;
      } else {
        return RoundParticipationType.other;
      }
    },
    [isNoneParticipation, isProRata]
  );

  return {
    getProRataValues,
    getNoneParticipationValues,
    isProRata,
    isNoneParticipation,
    getInitialParticipation,
  };
}
